<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import CollapsibleSection from './CollapsibleSection.vue';
import TabTableOfContents from './TabTableOfContents.vue';
import AllergenToggleGrid from './AllergenToggleGrid.vue';
import AllergenEntityRow from './AllergenEntityRow.vue';
import { RegulatoryDeclarationsService } from '@/services/regulatory-declarations.services';
import type {
  IAddonDeclarations,
  IDeclarationLine,
  IVariantDeclarations,
} from './regulatory-declarations.types';
import './epp-shared.css';

interface AllergenVariantRef {
  id?: string;
  _key: string;
  labelFr: string;
}

interface AllergenAddonOptionRef {
  id?: string;
  contentFr: string;
}

interface AllergenAddonGroupRef {
  id?: string;
  _key?: string;
  titleFr: string;
  options: AllergenAddonOptionRef[];
}

const props = defineProps<{
  productId: string;
  variants: AllergenVariantRef[];
  addonGroups: AllergenAddonGroupRef[];
}>();

const queryClient = useQueryClient();

interface AllergenAddonRow {
  rowId: string;
  addonId: string | null;
  label: string;
}

const addonRows = computed<AllergenAddonRow[]>(() =>
  props.addonGroups.flatMap((g, gIndex) =>
    g.options.map((opt, oIndex) => ({
      rowId: opt.id ?? `${g._key ?? g.id ?? gIndex}-opt-${oIndex}`,
      addonId: opt.id ?? null,
      label: `${g.titleFr || 'Groupe sans nom'} — ${opt.contentFr || 'Option sans nom'}`,
    })),
  ),
);

const loading = ref(true);
const loadError = ref<string | null>(null);
const saving = ref(false);
const saved = ref(false);
const saveError = ref<string | null>(null);

const productDeclarations = ref<IDeclarationLine[]>([]);
const variantDeclarations = ref<Record<string, IDeclarationLine[]>>({});
const addonDeclarations = ref<Record<string, IDeclarationLine[]>>({});

function variantRowId(v: AllergenVariantRef): string {
  return v.id ?? v._key;
}

function variantLines(v: AllergenVariantRef): IDeclarationLine[] {
  return variantDeclarations.value[variantRowId(v)] ?? [];
}
function addonLines(row: AllergenAddonRow): IDeclarationLine[] {
  return addonDeclarations.value[row.rowId] ?? [];
}

function setVariantLines(v: AllergenVariantRef, lines: IDeclarationLine[]) {
  variantDeclarations.value = {
    ...variantDeclarations.value,
    [variantRowId(v)]: lines,
  };
}
function setAddonLines(row: AllergenAddonRow, lines: IDeclarationLine[]) {
  addonDeclarations.value = {
    ...addonDeclarations.value,
    [row.rowId]: lines,
  };
}

async function load() {
  loading.value = true;
  loadError.value = null;
  try {
    const response =
      await RegulatoryDeclarationsService.getRegulatoryDeclarations(
        props.productId,
      );
    const tree = response.data.data;
    productDeclarations.value = tree.declarations ?? [];

    const variantMap: Record<string, IDeclarationLine[]> = {};
    for (const entry of tree.variants ?? []) {
      variantMap[entry.variantId] = entry.declarations;
    }
    variantDeclarations.value = variantMap;

    const addonMap: Record<string, IDeclarationLine[]> = {};
    for (const entry of tree.customAddons ?? []) {
      addonMap[entry.addonId] = entry.declarations;
    }
    addonDeclarations.value = addonMap;
  } catch {
    loadError.value =
      "Impossible de charger les déclarations d'allergènes de ce produit.";
  } finally {
    loading.value = false;
  }
}

onMounted(load);

const totalLines = computed(() => {
  const variantCount = Object.values(variantDeclarations.value).reduce(
    (sum, lines) => sum + lines.length,
    0,
  );
  const addonCount = Object.values(addonDeclarations.value).reduce(
    (sum, lines) => sum + lines.length,
    0,
  );
  return productDeclarations.value.length + variantCount + addonCount;
});

async function save() {
  saving.value = true;
  saved.value = false;
  saveError.value = null;
  try {
    const variantsPayload: IVariantDeclarations[] = props.variants
      .map((v) => ({
        variantId: variantRowId(v),
        declarations: variantLines(v),
      }))
      .filter((entry) => entry.declarations.length > 0);

    const addonsPayload: IAddonDeclarations[] = addonRows.value
      .filter(
        (row): row is AllergenAddonRow & { addonId: string } => !!row.addonId,
      )
      .map((row) => ({
        addonId: row.addonId,
        declarations: addonLines(row),
      }))
      .filter((entry) => entry.declarations.length > 0);

    await RegulatoryDeclarationsService.updateRegulatoryDeclarations(
      props.productId,
      {
        status: totalLines.value > 0 ? 'declared' : 'declared_none',
        declarations: productDeclarations.value,
        variants: variantsPayload,
        customAddons: addonsPayload,
      },
    );
    queryClient.invalidateQueries({
      queryKey: ['product-regulatory-declarations', props.productId],
    });
    saved.value = true;
    setTimeout(() => {
      saved.value = false;
    }, 2500);
  } catch {
    saveError.value = "Échec de l'enregistrement des allergènes.";
  } finally {
    saving.value = false;
  }
}

const sections = [
  { key: 'allergens-product', label: 'Produit' },
  { key: 'allergens-variants', label: 'Par variante' },
  { key: 'allergens-addons', label: "Par groupe d'options" },
];

const productSectionRef = ref<InstanceType<typeof CollapsibleSection> | null>(
  null,
);
const variantsSectionRef = ref<InstanceType<typeof CollapsibleSection> | null>(
  null,
);
const addonsSectionRef = ref<InstanceType<typeof CollapsibleSection> | null>(
  null,
);

const sectionRefs: Record<string, typeof productSectionRef> = {
  'allergens-product': productSectionRef,
  'allergens-variants': variantsSectionRef,
  'allergens-addons': addonsSectionRef,
};

function onJump(key: string) {
  const target = sectionRefs[key]?.value;
  if (target) target.isOpen = true;
  document
    .getElementById(`section-${key}`)
    ?.scrollIntoView({ behavior: 'smooth' });
}
</script>

<template>
  <div class="epp__allergens-tab">
    <TabTableOfContents :sections="sections" @jump="onJump" />

    <div class="aet__banner">
      <i class="pi pi-info-circle" />
      <p>
        Ces déclarations correspondent aux 14 allergènes à déclaration
        obligatoire du règlement UE 1169/2011. Elles sont indépendantes des
        autres informations du produit et disposent de leur propre
        enregistrement.
      </p>
    </div>

    <div v-if="loading" class="epp__empty">
      <i class="pi pi-spin pi-spinner epp__empty-icon" />
      <p>Chargement des allergènes…</p>
    </div>

    <div v-else-if="loadError" class="aet__error">
      <i class="pi pi-exclamation-triangle" />
      <span>{{ loadError }}</span>
    </div>

    <template v-else>
      <CollapsibleSection
        ref="productSectionRef"
        section-key="allergens-product"
        icon="pi-shield"
        title="Allergènes du produit"
        subtitle="Déclaration au niveau du produit de base"
        default-open
      >
        <AllergenToggleGrid v-model="productDeclarations" />
      </CollapsibleSection>

      <CollapsibleSection
        ref="variantsSectionRef"
        section-key="allergens-variants"
        icon="pi-list"
        title="Par variante"
        subtitle="Déclarations spécifiques à chaque variante"
      >
        <div v-if="variants.length" class="aet__rows">
          <AllergenEntityRow
            v-for="v in variants"
            :key="variantRowId(v)"
            :label="v.labelFr || 'Variante sans nom'"
            :model-value="variantLines(v)"
            @update:model-value="(lines) => setVariantLines(v, lines)"
          />
        </div>
        <div v-else class="epp__empty">
          <i class="pi pi-list epp__empty-icon" />
          <p>Ce produit n'a pas de variantes.</p>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        ref="addonsSectionRef"
        section-key="allergens-addons"
        icon="pi-th-large"
        title="Par groupe d'options"
        subtitle="Déclarations spécifiques à chaque groupe d'options"
      >
        <div v-if="addonRows.length" class="aet__rows">
          <AllergenEntityRow
            v-for="row in addonRows"
            :key="row.rowId"
            :label="row.label"
            :model-value="addonLines(row)"
            @update:model-value="(lines) => setAddonLines(row, lines)"
          />
        </div>
        <div v-else class="epp__empty">
          <i class="pi pi-th-large epp__empty-icon" />
          <p>Ce produit n'a pas de groupes d'options.</p>
        </div>
      </CollapsibleSection>

      <div v-if="saveError" class="aet__error">
        <i class="pi pi-exclamation-triangle" />
        <span>{{ saveError }}</span>
        <button class="aet__dismiss-btn" @click="saveError = null">
          <i class="pi pi-times" />
        </button>
      </div>
      <div v-if="saved" class="aet__success">
        <i class="pi pi-check-circle" />
        Allergènes enregistrés
      </div>

      <div class="aet__actions">
        <button
          class="epp__btn epp__btn--primary"
          :disabled="saving"
          @click="save"
        >
          <i v-if="saving" class="pi pi-spin pi-spinner" />
          <i v-else class="pi pi-save" />
          {{ saving ? 'Enregistrement…' : 'Enregistrer les allergènes' }}
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.epp__allergens-tab {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.aet__banner {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
  margin-bottom: 1rem;
}
.aet__banner i {
  color: #3b82f6;
  margin-top: 0.1rem;
  flex-shrink: 0;
}
.aet__banner p {
  margin: 0;
  line-height: 1.5;
}

.aet__rows {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.aet__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.25);
  color: #ef4444;
  font-size: 0.8125rem;
  margin: 1rem 0;
}
.aet__dismiss-btn {
  margin-left: auto;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
}
.aet__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(16, 181, 16, 0.08);
  border: 1px solid rgba(16, 181, 16, 0.25);
  color: #10b510;
  font-size: 0.8125rem;
  margin: 1rem 0;
}

.aet__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

[data-theme='dark'] .aet__banner {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.3);
}
</style>
